import React from "react";

import {
    Modal,
    Button,
    Table,
    Tag,
    Space,
    Typography,
    Empty,
    Card,
    Row,
    Col,
} from "antd";

import {
    FilePdfOutlined,
    HistoryOutlined,
    CalendarOutlined,
    UserOutlined,
    BookOutlined,
    CloseOutlined,
} from "@ant-design/icons";

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    PDFDownloadLink,
} from "@react-pdf/renderer";

import { motion } from "framer-motion";

const { Title, Text: AntText } = Typography;

/* =========================================================
   THEME
========================================================= */

const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C07500",
    background: "#F3F6FB",
    white: "#FFFFFF",
    text: "#243447",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#15803D",
};

/* =========================================================
   PDF STYLES
========================================================= */

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 32,
        paddingBottom: 35,
        paddingHorizontal: 28,
        fontSize: 9,
        fontFamily: "Helvetica",
        color: COLORS.text,
        backgroundColor: COLORS.white,
    },

    header: {
        backgroundColor: COLORS.navy,
        paddingVertical: 22,
        paddingHorizontal: 18,
        borderRadius: 5,
        marginBottom: 18,
        borderBottomWidth: 4,
        borderBottomColor: COLORS.gold,
    },

    headerTitle: {
        fontSize: 19,
        fontFamily: "Helvetica-Bold",
        color: COLORS.white,
        textAlign: "center",
        marginBottom: 8,
    },

    headerSubtitle: {
        fontSize: 10,
        color: "#E2E8F0",
        textAlign: "center",
        marginBottom: 5,
    },

    studentName: {
        fontSize: 13,
        fontFamily: "Helvetica-Bold",
        color: COLORS.gold,
        textAlign: "center",
        marginTop: 5,
    },

    sectionTitle: {
        fontSize: 12,
        fontFamily: "Helvetica-Bold",
        color: COLORS.navy,
        marginBottom: 5,
    },

    sectionSubtitle: {
        fontSize: 8,
        color: COLORS.muted,
        marginBottom: 12,
    },

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: COLORS.border,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: COLORS.navy,
        minHeight: 34,
        alignItems: "center",
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 32,
        alignItems: "center",
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },

    alternateRow: {
        backgroundColor: "#F3F6FB",
    },

    cell: {
        paddingVertical: 8,
        paddingHorizontal: 4,
        textAlign: "center",
        fontSize: 8,
        color: COLORS.text,
        borderRightWidth: 0.5,
        borderRightColor: COLORS.border,
    },

    headerCell: {
        paddingVertical: 9,
        paddingHorizontal: 3,
        textAlign: "center",
        fontSize: 7.5,
        fontFamily: "Helvetica-Bold",
        color: COLORS.white,
        borderRightWidth: 0.5,
        borderRightColor: "#47627B",
    },

    numberCell: {
        width: "7%",
    },

    previousClassCell: {
        width: "20%",
    },

    promotedClassCell: {
        width: "20%",
    },

    yearCell: {
        width: "19%",
    },

    divisionCell: {
        width: "18%",
    },

    dateCell: {
        width: "16%",
        borderRightWidth: 0,
    },

    emptyState: {
        paddingVertical: 28,
        paddingHorizontal: 10,
        textAlign: "center",
        color: COLORS.muted,
        fontSize: 10,
    },

    footer: {
        position: "absolute",
        bottom: 17,
        left: 28,
        right: 28,
        paddingTop: 7,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
    },

    footerText: {
        fontSize: 7,
        color: COLORS.muted,
    },
});

/* =========================================================
   HELPERS
========================================================= */

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleDateString();
};

const getSafeFileName = (name) => {
    const safeName = (name || "Student")
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");

    return `${safeName || "Student"}_promotion_history.pdf`;
};

/* =========================================================
   PROMOTION HISTORY PDF
========================================================= */

const PromotionPDF = ({ student }) => {
    const history = student?.promotion_history || [];

    return (
        <Document
            title={`Promotion History - ${student?.full_name || "Student"}`}
            author="School Management System"
            subject="Student Promotion History"
        >
            <Page
                size="A4"
                orientation="landscape"
                style={pdfStyles.page}
            >
                <View style={pdfStyles.header}>
                    <Text style={pdfStyles.headerTitle}>
                        STUDENT PROMOTION HISTORY
                    </Text>

                    <Text style={pdfStyles.headerSubtitle}>
                        Official Academic Promotion Record
                    </Text>

                    <Text style={pdfStyles.studentName}>
                        {student?.full_name || "-"}
                    </Text>
                </View>

                <View>
                    <Text style={pdfStyles.sectionTitle}>
                        Promotion Records
                    </Text>

                    <Text style={pdfStyles.sectionSubtitle}>
                        Summary of the student's recorded academic promotions.
                    </Text>
                </View>

                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.numberCell,
                            ]}
                        >
                            #
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.previousClassCell,
                            ]}
                        >
                            PREVIOUS CLASS
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.promotedClassCell,
                            ]}
                        >
                            PROMOTED CLASS
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.yearCell,
                            ]}
                        >
                            ACADEMIC YEAR
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.divisionCell,
                            ]}
                        >
                            DIVISION
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.dateCell,
                            ]}
                        >
                            PROMOTION DATE
                        </Text>
                    </View>

                    {history.length > 0 ? (
                        history.map((item, index) => (
                            <View
                                key={`${item.student_name || student?.full_name || "student"}-${index}`}
                                style={[
                                    pdfStyles.tableRow,
                                    ...(index % 2 === 1
                                        ? [pdfStyles.alternateRow]
                                        : []),
                                ]}
                                wrap={false}
                            >
                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.numberCell,
                                    ]}
                                >
                                    {index + 1}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.previousClassCell,
                                    ]}
                                >
                                    {item.previous_class || "-"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.promotedClassCell,
                                    ]}
                                >
                                    {item.grade || "-"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.yearCell,
                                    ]}
                                >
                                    {item.academic_year || "-"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.divisionCell,
                                    ]}
                                >
                                    {item.division || "-"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.dateCell,
                                    ]}
                                >
                                    {formatDate(item.promoted_date)}
                                </Text>
                            </View>
                        ))
                    ) : (
                        <Text style={pdfStyles.emptyState}>
                            No promotion history has been recorded for this student.
                        </Text>
                    )}
                </View>

                <View style={pdfStyles.footer} fixed>
                    <Text style={pdfStyles.footerText}>
                        STUDENT ACADEMIC RECORD
                    </Text>

                    <Text style={pdfStyles.footerText}>
                        Generated: {new Date().toLocaleDateString()}
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

/* =========================================================
   PROMOTION HISTORY MODAL
========================================================= */

const PromotionHistoryPDF = ({ open, student, onClose }) => {
    if (!student) {
        return null;
    }

    const history = student.promotion_history || [];

    const columns = [
        {
            title: "#",
            key: "index",
            width: 55,
            align: "center",
            render: (_, __, index) => (
                <AntText
                    strong
                    style={{
                        color: COLORS.muted,
                        fontSize: 12,
                    }}
                >
                    {index + 1}
                </AntText>
            ),
        },
        {
            title: "Student",
            dataIndex: "student_name",
            key: "student_name",
            width: 160,
            render: (value) => (
                <Space size={8}>
                    <span className="promotion-history-mini-icon">
                        <UserOutlined />
                    </span>

                    <AntText
                        strong
                        style={{ color: COLORS.navy }}
                    >
                        {value || student.full_name || "-"}
                    </AntText>
                </Space>
            ),
        },
        {
            title: "Previous Class",
            dataIndex: "previous_class",
            key: "previous_class",
            width: 140,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 6,
                        padding: "3px 8px",
                        color: COLORS.text,
                        background: "#F1F5F9",
                        border: `1px solid ${COLORS.border}`,
                        marginInlineEnd: 0,
                    }}
                >
                    {value || "-"}
                </Tag>
            ),
        },
        {
            title: "Promoted Class",
            dataIndex: "grade",
            key: "grade",
            width: 145,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 6,
                        padding: "3px 8px",
                        color: COLORS.orange,
                        background: "#FFF7E6",
                        border: "1px solid #F6D58B",
                        fontWeight: 600,
                        marginInlineEnd: 0,
                    }}
                >
                    {value || "-"}
                </Tag>
            ),
        },
        {
            title: "Academic Year",
            dataIndex: "academic_year",
            key: "academic_year",
            width: 135,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 6,
                        padding: "3px 8px",
                        color: COLORS.deepBlue,
                        background: "#EAF2FC",
                        border: "1px solid #CBDCF1",
                        marginInlineEnd: 0,
                    }}
                >
                    {value || "-"}
                </Tag>
            ),
        },
        {
            title: "Division",
            dataIndex: "division",
            key: "division",
            width: 125,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 6,
                        padding: "3px 8px",
                        color: COLORS.success,
                        background: "#ECFDF3",
                        border: "1px solid #BBF7D0",
                        marginInlineEnd: 0,
                    }}
                >
                    {value || "-"}
                </Tag>
            ),
        },
        {
            title: "Promotion Date",
            dataIndex: "promoted_date",
            key: "promoted_date",
            width: 135,
            render: (value) => (
                <Space size={6}>
                    <CalendarOutlined
                        style={{ color: COLORS.muted }}
                    />

                    <AntText
                        style={{
                            color: COLORS.text,
                            fontSize: 12,
                        }}
                    >
                        {formatDate(value)}
                    </AntText>
                </Space>
            ),
        },
    ];

    return (
        <>
            <style>
                {`
                    /*
                     * Set this variable to the actual dashboard sidebar width.
                     * Example: 250px sidebar => modal offset is 125px.
                     */
                    :root {
                        --promotion-sidebar-width: 250px;
                    }

                    /*
                     * Ant Design renders the modal into a portal attached
                     * to the document body. This class shifts the modal
                     * by half the sidebar width, centering it in the
                     * remaining dashboard content area.
                     */
                    .promotion-history-modal-root .ant-modal {
                        max-width: calc(
                            100vw - var(--promotion-sidebar-width, 250px) - 32px
                        );
                        margin-left: calc(
                            var(--promotion-sidebar-width, 250px) / 2
                        );
                    }

                    .promotion-history-modal-root .ant-modal-close {
                        top: 18px;
                        right: 18px;
                        color: #FFFFFF;
                        z-index: 5;
                    }

                    .promotion-history-modal-root .ant-modal-close:hover {
                        color: ${COLORS.gold};
                        background: rgba(255,255,255,0.12);
                    }

                    .promotion-history-modal-root .ant-table {
                        background: transparent;
                    }

                    .promotion-history-modal-root .ant-table-container {
                        border: 1px solid ${COLORS.border};
                        border-radius: 12px;
                        overflow: hidden;
                    }

                    .promotion-history-modal-root .ant-table-thead > tr > th {
                        background: #EDF2F8 !important;
                        color: ${COLORS.navy} !important;
                        font-size: 11px;
                        font-weight: 800;
                        text-transform: uppercase;
                        letter-spacing: 0.35px;
                        white-space: nowrap;
                        padding: 15px 12px !important;
                        border-bottom: 1px solid ${COLORS.border};
                    }

                    .promotion-history-modal-root .ant-table-tbody > tr > td {
                        padding: 14px 12px !important;
                        border-bottom: 1px solid #EDF1F6;
                        background: #FFFFFF;
                    }

                    .promotion-history-modal-root .ant-table-tbody > tr:hover > td {
                        background: #F5F8FD !important;
                    }

                    .promotion-history-modal-root .ant-table-tbody > tr:last-child > td {
                        border-bottom: none;
                    }

                    .promotion-history-mini-icon {
                        width: 30px;
                        height: 30px;
                        flex-shrink: 0;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        color: ${COLORS.deepBlue};
                        background: #EAF2FC;
                        border-radius: 8px;
                    }

                    .promotion-history-modal-root .ant-tag {
                        white-space: normal;
                    }

                    /*
                     * On narrow screens, remove the sidebar offset because
                     * the sidebar is commonly collapsed or hidden.
                     */
                    @media (max-width: 767px) {
                        .promotion-history-modal-root .ant-modal {
                            max-width: calc(100vw - 24px);
                            margin-left: 0;
                        }
                    }
                `}
            </style>

            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                centered
                width={1080}
                destroyOnClose
                maskClosable={false}
                rootClassName="promotion-history-modal-root"
                closeIcon={
                    <CloseOutlined
                        style={{
                            color: COLORS.white,
                            fontSize: 16,
                        }}
                    />
                }
                styles={{
                    header: {
                        display: "none",
                    },
                    body: {
                        padding: 0,
                        background: COLORS.background,
                        borderRadius: 16,
                        overflow: "hidden",
                    },
                    content: {
                        padding: 0,
                        overflow: "hidden",
                        borderRadius: 16,
                    },
                }}
            >
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 18,
                        scale: 0.98,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: 0.28,
                        ease: "easeOut",
                    }}
                >
                    {/* NAVY AND GOLD HEADER */}

                    <div
                        style={{
                            position: "relative",
                            overflow: "hidden",
                            padding: "27px 30px 25px",
                            color: COLORS.white,
                            background: `linear-gradient(120deg, ${COLORS.navy} 0%, ${COLORS.deepBlue} 72%, #0B4777 100%)`,
                            borderBottom: `4px solid ${COLORS.gold}`,
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                width: 180,
                                height: 180,
                                borderRadius: "50%",
                                border: "30px solid rgba(255,255,255,0.035)",
                                right: -45,
                                top: -80,
                                pointerEvents: "none",
                            }}
                        />

                        <div
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                gap: 16,
                                paddingRight: 25,
                            }}
                        >
                            <div
                                style={{
                                    width: 58,
                                    height: 58,
                                    minWidth: 58,
                                    borderRadius: 16,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: COLORS.gold,
                                    color: COLORS.navy,
                                    fontSize: 26,
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                                }}
                            >
                                <HistoryOutlined />
                            </div>

                            <div>
                                <AntText
                                    style={{
                                        display: "block",
                                        color: COLORS.gold,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: 1.8,
                                        textTransform: "uppercase",
                                        marginBottom: 5,
                                    }}
                                >
                                    ACADEMIC RECORDS
                                </AntText>

                                <Title
                                    level={3}
                                    style={{
                                        color: COLORS.white,
                                        fontSize: 25,
                                        fontWeight: 800,
                                        margin: 0,
                                        lineHeight: 1.25,
                                    }}
                                >
                                    Promotion History
                                </Title>

                                <AntText
                                    style={{
                                        display: "block",
                                        color: "#D7E4F2",
                                        fontSize: 12,
                                        marginTop: 6,
                                    }}
                                >
                                    Review and export the student's promotion records.
                                </AntText>
                            </div>
                        </div>
                    </div>

                    {/* MODAL CONTENT */}

                    <div
                        style={{
                            padding: "25px 28px 28px",
                        }}
                    >
                        {/* STUDENT SUMMARY */}

                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 14,
                                marginBottom: 22,
                                boxShadow: "0 3px 14px rgba(6,42,75,0.045)",
                                border: `1px solid ${COLORS.border}`,
                            }}
                            styles={{
                                body: {
                                    padding: "18px 20px",
                                },
                            }}
                        >
                            <Row
                                align="middle"
                                gutter={[16, 14]}
                            >
                                <Col flex="none">
                                    <div
                                        style={{
                                            width: 54,
                                            height: 54,
                                            borderRadius: 15,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#EAF2FC",
                                            color: COLORS.deepBlue,
                                            fontSize: 23,
                                        }}
                                    >
                                        <UserOutlined />
                                    </div>
                                </Col>

                                <Col flex="auto">
                                    <AntText
                                        style={{
                                            display: "block",
                                            fontSize: 10,
                                            color: COLORS.muted,
                                            fontWeight: 700,
                                            letterSpacing: 1,
                                            textTransform: "uppercase",
                                            marginBottom: 5,
                                        }}
                                    >
                                        STUDENT
                                    </AntText>

                                    <Title
                                        level={4}
                                        style={{
                                            color: COLORS.navy,
                                            margin: 0,
                                            fontSize: 19,
                                            fontWeight: 800,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {student.full_name || "-"}
                                    </Title>

                                    <AntText
                                        style={{
                                            color: COLORS.muted,
                                            fontSize: 12,
                                        }}
                                    >
                                        Complete academic promotion timeline
                                    </AntText>
                                </Col>

                                <Col>
                                    <div
                                        style={{
                                            textAlign: "center",
                                            padding: "10px 17px",
                                            borderRadius: 12,
                                            background: "#FFF8DF",
                                            border: "1px solid #F7E6A6",
                                        }}
                                    >
                                        <Title
                                            level={3}
                                            style={{
                                                margin: 0,
                                                color: COLORS.navy,
                                                fontWeight: 800,
                                                fontSize: 24,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {history.length}
                                        </Title>

                                        <AntText
                                            style={{
                                                color: COLORS.orange,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {history.length === 1
                                                ? "Record"
                                                : "Records"}
                                        </AntText>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* TABLE HEADING */}

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                flexWrap: "wrap",
                                marginBottom: 15,
                            }}
                        >
                            <div>
                                <Title
                                    level={5}
                                    style={{
                                        color: COLORS.navy,
                                        margin: 0,
                                        fontSize: 16,
                                        fontWeight: 800,
                                    }}
                                >
                                    <BookOutlined
                                        style={{
                                            color: COLORS.goldenYellow,
                                            marginRight: 9,
                                        }}
                                    />
                                    Promotion Records
                                </Title>

                                <AntText
                                    style={{
                                        display: "block",
                                        marginTop: 5,
                                        fontSize: 12,
                                        color: COLORS.muted,
                                    }}
                                >
                                    Previous class, new class, division and promotion date.
                                </AntText>
                            </div>

                            <Tag
                                style={{
                                    margin: 0,
                                    padding: "5px 10px",
                                    borderRadius: 7,
                                    border: `1px solid ${COLORS.border}`,
                                    color: COLORS.deepBlue,
                                    background: COLORS.white,
                                    fontSize: 11,
                                    fontWeight: 700,
                                }}
                            >
                                {history.length}{" "}
                                {history.length === 1 ? "ENTRY" : "ENTRIES"}
                            </Tag>
                        </div>

                        {/* PROMOTION TABLE */}

                        <Table
                            columns={columns}
                            dataSource={history}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 900 }}
                            rowKey={(record, index) =>
                                `${record.student_name || student.full_name || "student"}-${record.academic_year || "year"}-${index}`
                            }
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <div>
                                                <AntText
                                                    strong
                                                    style={{ color: COLORS.navy }}
                                                >
                                                    No promotion history found
                                                </AntText>

                                                <br />

                                                <AntText
                                                    type="secondary"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    Promotion records will appear here when available.
                                                </AntText>
                                            </div>
                                        }
                                    />
                                ),
                            }}
                        />

                        {/* ACTION BUTTONS */}

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                flexWrap: "wrap",
                                marginTop: 24,
                                paddingTop: 20,
                                borderTop: `1px solid ${COLORS.border}`,
                            }}
                        >
                            <AntText
                                style={{
                                    fontSize: 11,
                                    color: COLORS.muted,
                                }}
                            >
                                <HistoryOutlined
                                    style={{ marginRight: 6 }}
                                />
                                Student academic history
                            </AntText>

                            <Space size={10} wrap>
                                <Button
                                    size="large"
                                    onClick={onClose}
                                    icon={<CloseOutlined />}
                                    style={{
                                        height: 43,
                                        borderRadius: 9,
                                        paddingInline: 19,
                                        borderColor: COLORS.border,
                                        color: COLORS.text,
                                        fontWeight: 600,
                                    }}
                                >
                                    Close
                                </Button>

                                <PDFDownloadLink
                                    document={<PromotionPDF student={student} />}
                                    fileName={getSafeFileName(student.full_name)}
                                    style={{ textDecoration: "none" }}
                                >
                                    {({ loading }) => (
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<FilePdfOutlined />}
                                            loading={loading}
                                            style={{
                                                height: 43,
                                                borderRadius: 9,
                                                paddingInline: 20,
                                                background: COLORS.navy,
                                                borderColor: COLORS.navy,
                                                color: COLORS.white,
                                                fontWeight: 700,
                                                boxShadow:
                                                    "0 4px 12px rgba(6,42,75,0.15)",
                                            }}
                                        >
                                            {loading
                                                ? "Preparing PDF..."
                                                : "Download PDF"}
                                        </Button>
                                    )}
                                </PDFDownloadLink>
                            </Space>
                        </div>
                    </div>
                </motion.div>
            </Modal>
        </>
    );
};

export default PromotionHistoryPDF;